import { useState, useEffect } from 'react';
import { supabase } from './supabase';
import { Warehouse, Building2, Truck, MapPin, ChevronDown } from 'lucide-react';
import { useTheme } from './ThemeContext';

const locationTypeIcons = {
  warehouse: Warehouse,
  office: Building2,
  truck: Truck
};

const locationTypeLabels = {
  warehouse: 'Warehouse',
  office: 'Office',
  truck: 'Truck'
};

export default function LocationPicker({
  companyId,
  selectedLocationId,
  onChange,
  showAllOption = false,
  disabled = false,
  style = {}
}) {
  const { colors } = useTheme();
  const [locations, setLocations] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    if (companyId) {
      loadLocations();
    }
  }, [companyId]);

  const loadLocations = async () => {
    try {
      setLoading(true);
      const { data, error } = await supabase
        .from('locations')
        .select('*')
        .eq('company_id', companyId)
        .eq('is_active', true)
        .order('is_default', { ascending: false })
        .order('name');

      if (error) throw error;
      setLocations(data || []);

      // Auto-select default location if none selected
      if (!selectedLocationId && data?.length > 0 && !showAllOption) {
        const defaultLocation = data.find(l => l.is_default) || data[0];
        if (defaultLocation && onChange) {
          onChange(defaultLocation.id);
        }
      }
    } catch (error) {
      console.error('Error loading locations:', error);
    } finally {
      setLoading(false);
    }
  };

  const getLocationIcon = (type) => {
    const IconComponent = locationTypeIcons[type] || MapPin;
    return IconComponent;
  };

  const selectedLocation = locations.find(l => l.id === selectedLocationId);

  if (loading) {
    return (
      <div style={{
        padding: '0.75rem 1rem',
        background: colors.bgInput,
        border: `1px solid ${colors.border}`,
        borderRadius: '8px',
        color: colors.textMuted,
        ...style
      }}>
        Loading...
      </div>
    );
  }

  if (locations.length === 0) {
    return (
      <div style={{
        padding: '0.75rem 1rem',
        background: colors.bgInput,
        border: `1px solid ${colors.border}`,
        borderRadius: '8px',
        color: colors.textMuted,
        ...style
      }}>
        No locations
      </div>
    );
  }

  // For single location, just show the name
  if (locations.length === 1 && !showAllOption) {
    const loc = locations[0];
    const Icon = getLocationIcon(loc.location_type);
    return (
      <div style={{
        padding: '0.75rem 1rem',
        background: colors.bgInput,
        border: `1px solid ${colors.border}`,
        borderRadius: '8px',
        color: colors.text,
        display: 'flex',
        alignItems: 'center',
        gap: '0.5rem',
        ...style
      }}>
        <Icon size={18} color={colors.accent} />
        <span>{loc.name}</span>
        {loc.is_default && (
          <span style={{
            fontSize: '0.7rem',
            padding: '0.1rem 0.4rem',
            background: colors.accent,
            color: '#fff',
            borderRadius: '4px',
            marginLeft: 'auto'
          }}>
            Default
          </span>
        )}
      </div>
    );
  }

  return (
    <div style={{ position: 'relative', ...style }}>
      <select
        value={selectedLocationId || ''}
        onChange={(e) => onChange && onChange(e.target.value || null)}
        disabled={disabled}
        style={{
          width: '100%',
          padding: '0.75rem 2.5rem 0.75rem 2.5rem',
          background: colors.bgInput,
          border: `1px solid ${colors.border}`,
          borderRadius: '8px',
          color: colors.text,
          fontSize: '1rem',
          cursor: disabled ? 'not-allowed' : 'pointer',
          appearance: 'none',
          opacity: disabled ? 0.6 : 1
        }}
      >
        {showAllOption && (
          <option value="">All Locations</option>
        )}
        {locations.map(loc => (
          <option key={loc.id} value={loc.id}>
            {locationTypeLabels[loc.location_type] || 'Location'}: {loc.name}
            {loc.is_default ? ' (Default)' : ''}
          </option>
        ))}
      </select>

      {/* Location Icon */}
      <div style={{
        position: 'absolute',
        left: '0.75rem',
        top: '50%',
        transform: 'translateY(-50%)',
        pointerEvents: 'none',
        color: colors.accent
      }}>
        {selectedLocation ? (
          (() => {
            const Icon = getLocationIcon(selectedLocation.location_type);
            return <Icon size={18} />;
          })()
        ) : (
          <MapPin size={18} />
        )}
      </div>

      {/* Dropdown Arrow */}
      <div style={{
        position: 'absolute',
        right: '0.75rem',
        top: '50%',
        transform: 'translateY(-50%)',
        pointerEvents: 'none',
        color: colors.textMuted
      }}>
        <ChevronDown size={18} />
      </div>
    </div>
  );
}

export { locationTypeIcons, locationTypeLabels };
